import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MessageCircle, Share2, Bookmark, MoreHorizontal, Bot, Send, Paperclip, Mic, CornerDownLeft } from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Card } from "./ui/card";
import { useState, FormEvent } from "react";
import { PostModal } from "./PostModal";
import { PostsSidebar } from "./PostsSidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { ExpandableChat, ExpandableChatBody, ExpandableChatFooter, ExpandableChatHeader } from "./ui/expandable-chat";
import { ChatInput } from "./ui/chat-input";
import { ChatMessageList } from "./ui/chat-message-list";
import { ChatBubble, ChatBubbleAvatar, ChatBubbleMessage } from "./ui/chat-bubble";
import { PostsLoading } from "./ui/posts-loading";
import { HeartButton } from "./ui/heart-button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { useUser } from "@/hooks/use-user";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ShareMenu } from "./ui/ShareMenu";
import { SaveToCollection } from "./SaveToCollection";

type Post = {
  id: string;
  title: string;
  description: string;
  image_url: string;
  category: string;
  created_at: string;
  profiles: {
    username: string;
    avatar_url: string;
    id: string;
  };
};

type ChatMessage = {
  id: number;
  content: string;
  sender: "user" | "ai";
};

type Collection = {
  id: string;
  name: string;
  pins: {
    id: string;
  }[];
};

export const PostGrid = () => {
  const isMobile = useIsMobile();
  const {
    user
  } = useUser();
  const [selectedPostIndex, setSelectedPostIndex] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([{
    id: 1,
    content: "Olá! Como posso ajudar você hoje?",
    sender: "ai"
  }]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const collectionsQuery = useQuery({
    queryKey: ["pin_collections"],
    queryFn: async () => {
      try {
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (!session?.user) return [];
        const {
          data,
          error
        } = await supabase.from("pin_collections").select(`
            id, 
            name, 
            pins:pin_collection_items(
              pin_id, 
              collection_id
            )
          `).eq("user_id", session.user.id);
        if (error) throw error;
        const transformedCollections = data?.map(collection => ({
          ...collection,
          pins: collection.pins?.map((item: any) => ({
            id: item.pin_id
          })) || []
        })) || [];
        return transformedCollections;
      } catch (error) {
        console.error("Error fetching collections:", error);
        return [];
      }
    },
    enabled: !!user
  });

  const postsQuery = useQuery({
    queryKey: ["posts"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("posts").select(`
          id,
          title,
          description,
          image_url,
          category,
          created_at,
          user_id,
          profiles:user_id (username, avatar_url, id)
        `).order("created_at", {
        ascending: false
      });
      if (error) throw error;
      return data as Post[];
    },
    refetchOnWindowFocus: false
  });

  const likesQuery = useQuery({
    queryKey: ["post_likes"],
    queryFn: async () => {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) return {};

      const {
        data: likesData,
        error: likesError
      } = await supabase.from("pin_likes").select("pin_id");
      if (likesError) throw likesError;

      const countMap: Record<string, number> = {};
      likesData?.forEach(like => {
        const postId = like.pin_id;
        countMap[postId] = (countMap[postId] || 0) + 1;
      });

      const {
        data: userLikes,
        error: userLikesError
      } = await supabase.from("pin_likes").select("pin_id").eq("user_id", session.user.id);
      if (userLikesError) throw userLikesError;
      const userLikedPosts = new Set((userLikes || []).map(like => like.pin_id));

      const likesMap: Record<string, {
        count: number;
        userLiked: boolean;
      }> = {};
      Object.keys(countMap).forEach(postId => {
        likesMap[postId] = {
          count: countMap[postId] || 0,
          userLiked: userLikedPosts.has(postId)
        };
      });

      postsQuery.data?.forEach(post => {
        if (!likesMap[post.id]) {
          likesMap[post.id] = {
            count: 0,
            userLiked: false
          };
        }
      });
      return likesMap;
    },
    enabled: !!user && !!postsQuery.data
  });

  const commentsQuery = useQuery({
    queryKey: ["post_comments_count"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("pin_comments").select("pin_id");
      if (error) throw error;
      const countMap: Record<string, number> = {};
      data?.forEach(comment => {
        const postId = comment.pin_id;
        countMap[postId] = (countMap[postId] || 0) + 1;
      });
      return countMap;
    }
  });

  const handlePreviousPost = () => {
    if (selectedPostIndex === null || !postsQuery.data) return;
    const newIndex = selectedPostIndex > 0 ? selectedPostIndex - 1 : null;
    setSelectedPostIndex(newIndex);
  };

  const handleNextPost = () => {
    if (selectedPostIndex === null || !postsQuery.data) return;
    const newIndex = selectedPostIndex < postsQuery.data.length - 1 ? selectedPostIndex + 1 : null;
    setSelectedPostIndex(newIndex);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    setMessages(prev => [...prev, {
      id: prev.length + 1,
      content: input,
      sender: "user"
    }]);
    setInput("");
    setIsLoading(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: prev.length + 1,
        content: "Este é um exemplo de resposta da IA.",
        sender: "ai"
      }]);
      setIsLoading(false);
    }, 1000);
  };

  const handleLikePost = async (postId: string) => {
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    if (!session) {
      toast("Você precisa estar logado para curtir posts");
      return;
    }
    try {
      const {
        data: existingLike
      } = await supabase.from("pin_likes").select("*").eq("pin_id", postId).eq("user_id", session.user.id).maybeSingle();
      if (existingLike) {
        await supabase.from("pin_likes").delete().eq("pin_id", postId).eq("user_id", session.user.id);
      } else {
        await supabase.from("pin_likes").insert({
          pin_id: postId,
          user_id: session.user.id
        });
      }
      likesQuery.refetch();
      toast.success(existingLike ? "Post descurtido" : "Post curtido");
    } catch (error) {
      console.error("Error liking post:", error);
      toast.error("Erro ao curtir o post");
    }
  };

  if (postsQuery.isLoading) {
    return <PostsLoading />;
  }

  const getPostLikeCount = (postId: string) => {
    return likesQuery.data?.[postId]?.count || 0;
  };

  const isPostLikedByUser = (postId: string) => {
    return likesQuery.data?.[postId]?.userLiked || false;
  };

  const getPostCommentCount = (postId: string) => {
    return commentsQuery.data?.[postId] || 0;
  };

  const formattedPosts = postsQuery.data?.map(post => ({
    ...post,
    likes_count: getPostLikeCount(post.id),
    user_has_liked: isPostLikedByUser(post.id),
    comments_count: getPostCommentCount(post.id)
  }));

  const collections = collectionsQuery.data || [];

  return (
    <div className="relative max-w-[1200px] mx-auto px-4">
      <div className="flex justify-center">
        <div className="w-full max-w-[600px] space-y-6">
          {formattedPosts?.map((post, index) => (
            <Card 
              key={post.id} 
              className="overflow-hidden transition-all duration-300 hover:bg-gray-50 dark:hover:bg-secondary/50 animate-fade-in border-gray-200 dark:border-secondary"
            >
              <div className="p-4 py-[16px] px-[16px] mx-0 max-w-2xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Link to={`/profile/${post.profiles.id}`}>
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-gray-200 dark:border-primary/20 cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all">
                        <AvatarImage src={post.profiles.avatar_url} />
                        <AvatarFallback className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">
                          {post.profiles.username?.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </Link>
                    <div className="flex flex-col">
                      <Link to={`/profile/${post.profiles.id}`} className="hover:underline">
                        <h3 className="font-medium text-sm text-gray-900 dark:text-foreground">
                          {post.profiles.username}
                        </h3>
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-muted-foreground">
                        {new Date(post.created_at).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric'
                    })}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="text-gray-500 dark:text-muted-foreground hover:text-primary">
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </div>

                {post.title && <h2 className="text-lg font-semibold mb-2">{post.title}</h2>}

                {post.description && <p className="mb-3 text-sm leading-relaxed text-gray-700 dark:text-foreground">{post.description}</p>}

                {post.category && <div className="mb-3">
                    <Badge variant="secondary" className="text-xs">
                      #{post.category}
                    </Badge>
                  </div>}

                {post.image_url && <div className="relative mb-4 rounded-lg overflow-hidden group cursor-pointer" onClick={() => setSelectedPostIndex(index)}>
                    <img src={post.image_url} alt={post.title || post.description || "Post image"} loading="lazy" style={{
                maxHeight: "600px",
                maxWidth: "100%"
              }} className="w-full h-auto mx-auto object-cover" />
                  </div>}

                <div className="flex items-center gap-6">
                  <HeartButton initialCount={getPostLikeCount(post.id)} isLiked={isPostLikedByUser(post.id)} onToggle={() => handleLikePost(post.id)} />
                  <Button variant="ghost" size="sm" className="text-gray-500 dark:text-muted-foreground hover:text-primary hover:bg-primary/10 gap-2" onClick={() => setSelectedPostIndex(index)}>
                    <MessageCircle className="h-4 w-4" />
                    <span className="text-xs">{getPostCommentCount(post.id)}</span>
                  </Button>
                  <ShareMenu title={post.title || ""} url={`${window.location.origin}/pin/${post.id}`} imageUrl={post.image_url} />
                  {user && (
                    <SaveToCollection postId={post.id} />
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {!isMobile && (
          <div className="hidden lg:block lg:w-1/3 ml-6 sticky top-20 self-start">
            <PostsSidebar />
          </div>
        )}
      </div>

      <PostModal post={selectedPostIndex !== null && formattedPosts ? formattedPosts[selectedPostIndex] : null} isOpen={selectedPostIndex !== null} onClose={() => setSelectedPostIndex(null)} onPrevious={handlePreviousPost} onNext={handleNextPost} hasPrevious={selectedPostIndex !== null && selectedPostIndex > 0} hasNext={selectedPostIndex !== null && formattedPosts && selectedPostIndex < formattedPosts.length - 1} onLike={postId => handleLikePost(postId)} />

      <ExpandableChat size="lg" position="bottom-right" icon={<Bot className="h-6 w-6" />}>
        <ExpandableChatHeader className="flex-col text-center justify-center">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Chat com IA ✨</h1>
          <p className="text-sm text-gray-500 dark:text-muted-foreground">
            Como posso ajudar você hoje?
          </p>
        </ExpandableChatHeader>

        <ExpandableChatBody>
          <ChatMessageList>
            {messages.map(message => <ChatBubble key={message.id} variant={message.sender === "user" ? "sent" : "received"}>
                <ChatBubbleAvatar className="h-8 w-8 shrink-0" src={message.sender === "user" ? "https://github.com/shadcn.png" : undefined} fallback={message.sender === "user" ? "US" : "AI"} />
                <ChatBubbleMessage variant={message.sender === "user" ? "sent" : "received"}>
                  {message.content}
                </ChatBubbleMessage>
              </ChatBubble>)}

            {isLoading && <ChatBubble variant="received">
                <ChatBubbleAvatar className="h-8 w-8 shrink-0" fallback="AI" />
                <ChatBubbleMessage isLoading />
              </ChatBubble>}
          </ChatMessageList>
        </ExpandableChatBody>

        <ExpandableChatFooter>
          <form onSubmit={handleSubmit} className="relative rounded-lg border bg-white dark:bg-background focus-within:ring-1 focus-within:ring-ring p-1">
            <ChatInput value={input} onChange={e => setInput(e.target.value)} placeholder="Digite sua mensagem..." className="min-h-12 resize-none rounded-lg bg-white dark:bg-background border-0 p-3 shadow-none focus-visible:ring-0" />
            <div className="flex items-center p-3 pt-0 justify-between">
              <div className="flex">
                <Button variant="ghost" size="icon" type="button">
                  <Paperclip className="h-4 w-4" />
                </Button>

                <Button variant="ghost" size="icon" type="button">
                  <Mic className="h-4 w-4" />
                </Button>
              </div>
              <Button type="submit" size="sm" className="ml-auto gap-1.5">
                Enviar
                <CornerDownLeft className="h-3.5 w-3.5" />
              </Button>
            </div>
          </form>
        </ExpandableChatFooter>
      </ExpandableChat>
    </div>
  );
};
